import { useQueryClient } from "@tanstack/react-query"
import type { Message } from "ably"
import type { ReactNode } from "react"
import { createContext, useContext, useEffect, useRef } from "react"
import { useAuthStore } from "@/features/auth/store/authStore"
import type { RealtimeConnectionStatus } from "@/hooks/useAblyRealtimeClient"
import { useRealtimeChannelMessages, useRealtimeConnection } from "./RealtimeContext"

interface NotificationRealtimeContextType {
  isConnected: boolean
  connectionStatus: RealtimeConnectionStatus
}

const NotificationRealtimeContext = createContext<NotificationRealtimeContextType>({
  isConnected: false,
  connectionStatus: "disconnected",
})

interface NotificationRealtimeProviderProps {
  children: ReactNode
}

export const NotificationRealtimeProvider = ({
  children,
}: NotificationRealtimeProviderProps): ReactNode => {
  const user = useAuthStore(state => state.user)
  const queryClient = useQueryClient()
  const { isConnected, connectionStatus } = useRealtimeConnection()

  // The unread counts are kept fresh by realtime pushes (chat:unread_count_updated
  // / notification:unread_count_updated), but a push can be missed while the
  // socket is down. Resync on reconnect so a count can't stay stale -- this
  // replaces the per-tab-focus refetch useChatUnreadCount used to do (issue #528).
  const wasConnectedRef = useRef(isConnected)
  useEffect(() => {
    if (isConnected && !wasConnectedRef.current) {
      void queryClient.invalidateQueries({ queryKey: ["chat-unread-count"] })
      void queryClient.invalidateQueries({ queryKey: ["unread-count"] })
      void queryClient.invalidateQueries({ queryKey: ["notifications"] })
    }
    wasConnectedRef.current = isConnected
  }, [isConnected, queryClient])

  const handleMessage = (message: Message) => {
    try {
      switch (message.name) {
        // The notification list and unread count are tab-scoped queries
        // (`["notifications", tab]` / `["unread-count", tab]` -- see
        // useNotifications). Invalidate by prefix so every mounted tab refetches
        // and the feed reflects the change in realtime. (These previously wrote
        // optimistically to non-tab-scoped keys and silently no-op'd, so realtime
        // notifications only appeared after a manual refresh.)
        case "notification:new":
        case "notification:action_taken":
        case "notification:marked_read":
        case "notification:marked_unread":
        case "notification:marked_all_read":
        case "notification:removed":
        case "notification:unread_count_updated": {
          void queryClient.invalidateQueries({ queryKey: ["notifications"] })
          void queryClient.invalidateQueries({ queryKey: ["unread-count"] })
          break
        }
        // Chat unread count is a single, non-tab-scoped query, so a direct cache
        // write is correct here.
        case "chat:unread_count_updated": {
          const data = message.data as { count: number }
          queryClient.setQueryData(["chat-unread-count"], data.count)
          break
        }
      }
    } catch (error) {
      console.error("Failed to process realtime notification message", {
        eventName: message.name,
        error: error instanceof Error ? error.message : String(error),
      })
    }
  }

  useRealtimeChannelMessages(user ? `user:${user.id}` : null, handleMessage)

  return (
    <NotificationRealtimeContext.Provider value={{ isConnected, connectionStatus }}>
      {children}
    </NotificationRealtimeContext.Provider>
  )
}

export const useNotificationRealtime = (): NotificationRealtimeContextType =>
  useContext(NotificationRealtimeContext)
